'use client'

import { cn } from '../../utils/cn'
import { ArrowRight } from 'lucide-react'

interface HeroBadge {
  text: string
  href?: string
}

interface HeroCTA {
  text: string
  href: string
}

interface HeroScreenshot {
  src: string
  alt: string
}

interface HeroSimpleProps {
  badge?: HeroBadge
  headline: string
  subheadline: string
  primaryCTA: HeroCTA
  secondaryCTA?: HeroCTA
  screenshot?: HeroScreenshot
  avatars?: string[]
  className?: string
}

export function HeroSimple({
  badge,
  headline,
  subheadline,
  primaryCTA,
  secondaryCTA,
  screenshot,
  avatars,
  className
}: HeroSimpleProps) {
  return (
    <section className={cn("relative min-h-[90vh] flex flex-col items-center justify-center pt-24 pb-16 overflow-hidden", className)}>
      {/* Grid background pattern */}
      <div className="absolute inset-0 grid-pattern opacity-15" />

      {/* Hero glow effect from top */}
      <div className="hero-glow" />

      {/* Secondary radial gradient for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 50% at 50% -10%, var(--accent-glow), transparent 60%)`
        }}
      />

      {/* Bottom fade to background */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[var(--landing-bg)] to-transparent pointer-events-none" />

      <div className="landing-container relative z-10">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
          {/* Badge */}
          {badge && (
            <a
              href={badge.href}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-1.5 mb-8',
                'text-sm font-medium',
                'bg-[var(--landing-bg-card)] border border-[var(--landing-border)]',
                'rounded-full',
                'text-[var(--landing-text-secondary)]',
                'hover:border-[var(--accent)] hover:text-[var(--landing-text-primary)]',
                'transition-all duration-200',
                'group'
              )}
            >
              <span className="w-2 h-2 rounded-full bg-[var(--accent)] badge-pulse" />
              {badge.text}
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </a>
          )}

          {/* Headline */}
          <h1 className="headline-xl mb-6">
            {headline}
          </h1>

          {/* Subheadline */}
          <p className="body-lg max-w-readable mb-8">
            {subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
            <a
              href={primaryCTA.href}
              className={cn(
                'inline-flex items-center justify-center gap-2',
                'px-6 py-3 text-base font-medium rounded-lg',
                'bg-[var(--accent)] text-white',
                'hover:bg-[var(--accent-hover)]',
                'transition-all duration-200',
                'cta-glow'
              )}
            >
              {primaryCTA.text}
              <ArrowRight className="h-4 w-4" />
            </a>
            {secondaryCTA && (
              <a
                href={secondaryCTA.href}
                className={cn(
                  'inline-flex items-center justify-center gap-2',
                  'px-6 py-3 text-base font-medium rounded-lg',
                  'btn-secondary',
                  'hover:border-[var(--landing-border-accent)]',
                  'transition-all duration-200'
                )}
              >
                {secondaryCTA.text}
              </a>
            )}
          </div>

          {/* Avatar Stack (Social Proof) */}
          {avatars && avatars.length > 0 && (
            <div className="flex items-center gap-3 mb-12">
              <div className="flex -space-x-2">
                {avatars.slice(0, 5).map((avatar, index) => (
                  <img
                    key={index}
                    src={avatar}
                    alt=""
                    className="w-8 h-8 rounded-full border-2 border-[var(--landing-bg)]"
                  />
                ))}
              </div>
              <span className="text-sm text-[var(--landing-text-muted)]">
                Join {avatars.length > 100 ? '100+' : avatars.length} others
              </span>
            </div>
          )}
        </div>

        {/* Screenshot */}
        {screenshot && (
          <div className="relative mt-8 max-w-5xl mx-auto">
            <div className="screenshot overflow-hidden">
              <img
                src={screenshot.src}
                alt={screenshot.alt}
                className="w-full h-auto"
              />
            </div>
            {/* Fade gradient at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[var(--landing-bg)] to-transparent" />
          </div>
        )}
      </div>
    </section>
  )
}
